import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CheckCircle, XCircle, ShieldCheck, AlertTriangle, Clock } from 'lucide-react';
import { verificationAcknowledgementService, getErrorMessage } from '../../services/apiService';

/**
 * The vendor's side of self-regularization: after Amber verified their details against the
 * registries and wrote them to the record, the vendor is shown exactly what changed and asked to
 * confirm it — or to say what is wrong. One link, nothing to fill in. Public: the token in the
 * link is the only credential.
 */

const formatDate = (iso) => (iso ? new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' }) : '');

const Screen = ({ children }) => (
  <div className="flex min-h-[100dvh] items-center justify-center bg-gray-50 px-4 py-8">
    <div className="w-full max-w-2xl">{children}</div>
  </div>
);

const Card = ({ children }) => <div className="rounded-2xl bg-white p-6 shadow-lg sm:p-8">{children}</div>;

function Outcome({ icon: IconC, tone, title, children }) {
  const color = tone === 'green' ? 'text-emerald-500' : tone === 'red' ? 'text-red-500' : tone === 'amber' ? 'text-amber-500' : 'text-gray-400';
  return (
    <Screen>
      <Card>
        <div className="text-center">
          <IconC className={`mx-auto mb-4 h-16 w-16 ${color}`} />
          <h1 className="text-2xl font-bold text-gray-900">{title}</h1>
          <div className="mt-2 text-gray-600">{children}</div>
        </div>
      </Card>
    </Screen>
  );
}

const VendorVerification = () => {
  const [params] = useSearchParams();
  const token = params.get('token');

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [disputing, setDisputing] = useState(false);
  const [name, setName] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState(null);

  useEffect(() => {
    if (!token || token.length < 20) {
      setError('This link is not valid. Please use the link from your email.');
      setLoading(false);
      return;
    }
    (async () => {
      try {
        const res = await verificationAcknowledgementService.get(token);
        setData(res.data);
      } catch (err) {
        setError(getErrorMessage(err) || 'This link could not be opened.');
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const respond = async (answer) => {
    setBusy(true);
    setProblem(null);
    try {
      const res = await verificationAcknowledgementService.respond(token, { answer, name: name.trim() || undefined, note: note.trim() || undefined });
      setData(res.data);
    } catch (err) {
      setProblem(getErrorMessage(err) || 'Your answer could not be recorded. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <Screen>
        <div className="text-center">
          <div className="mx-auto h-12 w-12 animate-spin rounded-full border-b-2 border-blue-600" />
          <p className="mt-4 text-gray-600">Opening your verification…</p>
        </div>
      </Screen>
    );
  }
  if (error) {
    return (
      <Outcome icon={XCircle} tone="red" title="Link not valid">
        <p>{error}</p>
      </Outcome>
    );
  }
  if (data.status === 'acknowledged') {
    return (
      <Outcome icon={CheckCircle} tone="green" title="Thank you — details confirmed">
        <p>
          {data.company_name}'s details were confirmed on {formatDate(data.responded_at)}. Nothing more is needed from you.
        </p>
      </Outcome>
    );
  }
  if (data.status === 'disputed') {
    return (
      <Outcome icon={AlertTriangle} tone="amber" title="Thank you — we will look into it">
        <p>You told us on {formatDate(data.responded_at)} that something in these details is wrong. Amber's vendor team will correct the record and be in touch if anything is needed.</p>
      </Outcome>
    );
  }
  if (data.status === 'expired') {
    return (
      <Outcome icon={Clock} tone="gray" title="This link has expired">
        <p>It was valid until {formatDate(data.expires_at)}. Please ask Amber's vendor team for a new one.</p>
      </Outcome>
    );
  }

  const written = data.written || [];
  const checks = data.checks || [];

  return (
    <Screen>
      <Card>
        <div className="flex items-start gap-4">
          <ShieldCheck className="mt-1 h-10 w-10 shrink-0 text-blue-600" />
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-amber-600">Amber Enterprises India Limited</p>
            <h1 className="mt-1 text-2xl font-bold text-gray-900">Please confirm your details</h1>
            <p className="mt-2 text-gray-600">
              We verified <span className="font-semibold text-gray-900">{data.company_name}</span>
              {data.vendor_code ? <> (vendor code <span className="font-mono">{data.vendor_code}</span>)</> : null} against the government registries
              {data.verified_on ? <> on {formatDate(data.verified_on)}</> : null} and updated our records to match. Please check them below.
            </p>
          </div>
        </div>

        {checks.length > 0 && (
          <div className="mt-6 flex flex-wrap gap-2">
            {checks.map((c) => (
              <span key={c} className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-3 py-1 text-sm font-medium text-emerald-700">
                <CheckCircle className="h-4 w-4" /> {c} verified
              </span>
            ))}
          </div>
        )}

        <div className="mt-6 rounded-xl border border-gray-200 bg-gray-50">
          <div className="border-b border-gray-200 px-4 py-2.5 text-xs font-bold uppercase tracking-wider text-gray-500">
            {written.length > 0 ? 'What we updated on your record' : 'What we checked'}
          </div>
          {written.length > 0 ? (
            <dl className="divide-y divide-gray-200">
              {written.map((w) => (
                <div key={w.field} className="grid grid-cols-1 gap-1 px-4 py-3 sm:grid-cols-[40%_1fr] sm:gap-4">
                  <dt className="text-sm text-gray-500">{w.label}</dt>
                  <dd className="text-sm font-semibold text-gray-900">{w.to ?? '—'}</dd>
                </div>
              ))}
            </dl>
          ) : (
            <p className="px-4 py-4 text-sm text-gray-600">Everything on your record already matched the registries — nothing needed changing.</p>
          )}
        </div>

        {problem && (
          <div className="mt-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700" role="alert">
            {problem}
          </div>
        )}

        <div className="mt-6">
          <label className="block text-sm font-medium text-gray-700" htmlFor="ack-name">
            Your name <span className="font-normal text-gray-400">(optional)</span>
          </label>
          <input
            id="ack-name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={120}
            placeholder="Who is confirming on behalf of the company"
            className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
          />
        </div>

        {!disputing ? (
          <div className="mt-6 flex flex-col gap-3 sm:flex-row">
            <button
              type="button"
              onClick={() => respond('confirm')}
              disabled={busy}
              className="inline-flex flex-1 items-center justify-center gap-2 rounded-lg bg-blue-600 px-5 py-3 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60"
            >
              <CheckCircle className="h-5 w-5" /> {busy ? 'Recording…' : 'These details are correct'}
            </button>
            <button
              type="button"
              onClick={() => setDisputing(true)}
              disabled={busy}
              className="inline-flex flex-1 items-center justify-center gap-2 rounded-lg border border-gray-300 bg-white px-5 py-3 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-60"
            >
              <AlertTriangle className="h-5 w-5 text-amber-500" /> Something is wrong
            </button>
          </div>
        ) : (
          <div className="mt-6 rounded-xl border border-amber-200 bg-amber-50 p-4">
            <label className="block text-sm font-semibold text-gray-900" htmlFor="ack-note">
              What is wrong?
            </label>
            <p className="mt-0.5 text-xs text-gray-600">Tell us which detail and what it should be. Amber's vendor team will correct the record.</p>
            <textarea
              id="ack-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={4}
              maxLength={2000}
              placeholder="For example: our bank branch moved to Pune in March; the IFSC is now…"
              className="mt-2 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-100"
            />
            <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:justify-end">
              <button type="button" onClick={() => setDisputing(false)} disabled={busy} className="rounded-lg px-4 py-2 text-sm font-medium text-gray-600 hover:bg-white">
                Back
              </button>
              <button
                type="button"
                onClick={() => respond('dispute')}
                disabled={busy || note.trim().length < 5}
                className="rounded-lg bg-amber-600 px-5 py-2 text-sm font-semibold text-white hover:bg-amber-700 disabled:opacity-50"
              >
                {busy ? 'Sending…' : 'Report the problem'}
              </button>
            </div>
          </div>
        )}

        <p className="mt-6 text-xs text-gray-500">
          This link is unique to your company and expires on {formatDate(data.expires_at)}. Nothing on your record changes when you confirm; it is our record of your agreement.
        </p>
      </Card>
    </Screen>
  );
};

export default VendorVerification;
